/**
 * Cognito hosted-UI POPUP sign-in for the EMBEDDED surface (Genesys iframe).
 *
 * Pattern B + embedded: the BFF is the OIDC confidential client, but the app
 * runs inside a cross-origin Genesys iframe and Cognito sends X-Frame-Options,
 * so the hosted UI cannot render in an in-iframe redirect. Instead the SPA opens
 * `/auth/login?popup=1` in a popup window; the BFF runs the code+PKCE exchange,
 * sets the session cookie (SameSite=None; shared with the iframe), and serves a
 * page that postMessages the opener and closes the popup. The opener then
 * re-fetches `/api/session`.
 *
 * Auto-close is driven by POLLING `/api/session` (the authoritative signal) and
 * closing the popup FROM THE OPENER — mirroring the proven Genesys-link flow
 * ({@link linkGenesysViaPopup}). We do NOT rely on the done-page's own
 * `window.close()` nor on `postMessage`: inside a sandboxed Genesys iframe the
 * self-close is unreliable and COOP can sever `window.opener`, so the popup
 * lingered (the Cognito popup did not auto-close while the Genesys one did). The
 * postMessage + `popup.closed` paths are kept as accelerators only.
 */
import { fetchSession } from './session.js';

const POLL_INTERVAL_MS = 1000;
const LOGIN_TIMEOUT_MS = 120_000;

/** Re-reads `/api/session`; true once the BFF holds an authenticated session. */
async function isAuthenticated(): Promise<boolean> {
  try {
    return Boolean((await fetchSession()).upn);
  } catch {
    // NotAuthenticatedError (embedded 401) or a transient failure — not yet.
    return false;
  }
}

export function openLoginPopup(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const returnTo = encodeURIComponent('/');
    const popup = window.open(
      `/auth/login?popup=1&returnTo=${returnTo}`,
      'ptsb-cognito-login',
      'popup,width=480,height=720,noopener=no',
    );
    if (!popup) {
      reject(new Error('Sign-in popup was blocked. Allow popups and retry.'));
      return;
    }

    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closedPoll);
      clearInterval(sessionPoll);
      // #430 — close the popup from the OPENER by its handle. The done-page's own
      // `window.close()` is unreliable in the embedded sandbox; with COOP
      // `same-origin-allow-popups` on the opener the handle stays valid, so this
      // reliably dismisses it. Best-effort — if the handle is severed it no-ops
      // and the done-page's "you can close this window" message covers it.
      try {
        popup.close();
      } catch {
        /* handle severed — the done-page self-close / message covers it */
      }
      resolve();
    };

    const onMessage = (e: MessageEvent): void => {
      if (e.origin !== window.location.origin) return;
      if (
        e.data &&
        typeof e.data === 'object' &&
        (e.data as { type?: string }).type === 'ptsb-auth-complete'
      ) {
        finish();
      }
    };
    window.addEventListener('message', onMessage);

    // AUTHORITATIVE signal: poll the server session. Once the BFF has established
    // the session (cookie shared with the iframe), close the popup from here —
    // independent of the done-page self-close and of COOP severing postMessage.
    const deadline = Date.now() + LOGIN_TIMEOUT_MS;
    const sessionPoll = setInterval(() => {
      if (Date.now() > deadline) {
        // Give up polling; the popup/postMessage fallbacks may still resolve, but
        // stop hammering /api/session. The caller re-checks the session anyway.
        clearInterval(sessionPoll);
        return;
      }
      void isAuthenticated().then((ok) => {
        if (ok) finish();
      });
    }, POLL_INTERVAL_MS);

    // Fallback: the popup closing means the flow finished (the BFF callback
    // closes it, or the user dismissed it). COOP can block the postMessage, so
    // closure is a reliable signal — the caller re-fetches the session regardless.
    const closedPoll = setInterval(() => {
      if (popup.closed) finish();
    }, 500);
  });
}
